import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { AccountShell, useAccountShell } from '../components/AccountShell';
import PasswordInput from '../components/PasswordInput';
import { User, Mail, Phone, Lock, CheckCircle2, ArrowLeft } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Two-letter uppercase initials from a display name. Used for the
// avatar tile at the top of the account page — matches the same
// initials treatment used in the sidebar UserChip so the two are
// visually consistent.
const initialsOf = (s) =>
  (s || '').split(/\s+/).filter(Boolean).map(w => w[0]).slice(0, 2).join('').toUpperCase() || '?';

// Batch 3.4a — Shop admin (or master admin, or barber) editing their
// OWN account details. Direct edits allowed on shop-owned display data
// (name / phone). Email and password go through Supabase's own verify
// flows so a typo can't lock the user out.
const AccountInner = () => {
  const { profile, backHref, backLabel } = useAccountShell();
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [flash, setFlash] = useState('');
  const [error, setError] = useState('');

  // Profile edit
  const [fullName, setFullName] = useState('');
  const [phone, setPhone] = useState('');

  // Email change
  const [pendingEmail, setPendingEmail] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [emailBusy, setEmailBusy] = useState(false);

  // Password change
  const [currentPw, setCurrentPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [pwBusy, setPwBusy] = useState(false);

  useEffect(() => { (async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;
      // Auto-heal: if the user just clicked a Supabase email-change
      // confirmation link, mirror the new address into our own tables.
      try {
        await fetch(`${BACKEND_URL}/api/auth/me/sync-email`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${session.access_token}` },
        });
      } catch { /* fire-and-forget: sync is best-effort on mount */ }
      const { data: u } = await supabase.auth.getUser();
      const authEmail = u?.user?.email || '';
      const meta = u?.user?.new_email || '';
      setMe({ email: authEmail });
      setPendingEmail(meta || '');
      // user_profiles for name/phone
      const { data: p } = await supabase
        .from('user_profiles')
        .select('full_name,phone')
        .eq('id', session.user.id)
        .maybeSingle();
      setFullName(p?.full_name || '');
      setPhone(p?.phone || '');
    } finally {
      setLoading(false);
    }
  })(); }, []);

  const saveProfile = async () => {
    setSaving(true); setFlash(''); setError('');
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch(`${BACKEND_URL}/api/auth/me/profile`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
        body: JSON.stringify({ full_name: fullName, phone }),
      });
      const raw = await res.text();
      let body = {}; try { body = raw ? JSON.parse(raw) : {}; } catch { body = { detail: raw }; }
      if (!res.ok) throw new Error(body.detail || `Save failed (${res.status})`);
      setFlash('Profile saved.');
      setTimeout(() => setFlash(''), 2500);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const requestEmailChange = async () => {
    if (!newEmail) return;
    setEmailBusy(true); setError(''); setFlash('');
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch(`${BACKEND_URL}/api/auth/me/email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
        body: JSON.stringify({ new_email: newEmail }),
      });
      const raw = await res.text();
      let body = {}; try { body = raw ? JSON.parse(raw) : {}; } catch { body = { detail: raw }; }
      if (!res.ok) throw new Error(body.detail || `Email change refused (${res.status})`);
      setPendingEmail(newEmail);
      setNewEmail('');
      setFlash(body.message || 'Verification sent to the new address.');
    } catch (e) {
      setError(e.message);
    } finally {
      setEmailBusy(false);
    }
  };

  const changePassword = async () => {
    if (!currentPw || !newPw) return;
    if (newPw.length < 8) { setError('New password must be at least 8 characters.'); return; }
    setPwBusy(true); setError(''); setFlash('');
    try {
      // 1) Re-authenticate with the current password to verify identity.
      const { error: reErr } = await supabase.auth.signInWithPassword({
        email: me.email,
        password: currentPw,
      });
      if (reErr) throw new Error('Current password is incorrect.');
      // 2) Rotate to the new password.
      const { error: upErr } = await supabase.auth.updateUser({ password: newPw });
      if (upErr) throw upErr;
      setCurrentPw(''); setNewPw('');
      setFlash('Password updated. You\u2019ll stay signed in on this device.');
      // Clear must_change_password flag on the profile too.
      const { data: { session } } = await supabase.auth.getSession();
      try {
        await fetch(`${BACKEND_URL}/api/auth/mark-password-changed`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${session.access_token}` },
        });
      } catch { /* fire-and-forget: clearing must_change_password flag is best-effort */ }
    } catch (e) {
      setError(e.message);
    } finally {
      setPwBusy(false);
    }
  };

  if (loading) {
    return <div className="body" style={{ padding: 60, textAlign: 'center', color: 'var(--mute)' }}>Loading…</div>;
  }

  return (
    <main className="main">
      <div className="topbar" style={{ flexDirection: 'row', alignItems: 'center', flexWrap: 'nowrap' }}>
        <div
          className="account-avatar"
          data-testid="account-avatar"
          aria-hidden="true"
          style={{
            width: 44,
            height: 44,
            borderRadius: '50%',
            background: 'var(--acc-soft, #eef1ff)',
            color: 'var(--acc, #4f46e5)',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontWeight: 700,
            fontSize: 15,
            letterSpacing: '0.02em',
            marginRight: 12,
            flex: '0 0 44px',
          }}
        >
          {initialsOf(fullName || profile?.full_name || me?.email)}
        </div>
        <div style={{ flex: '1 1 auto', minWidth: 0 }}>
          <h2>My account</h2>
          <div className="sub">Edit your own profile, email and password. Barber account details for your staff are on the Barbers page.</div>
        </div>
        {/* Mobile-only Back button, aligned right inline with the
            heading. Desktop hides this because the sidebar already
            surfaces the same link (`.back-to-platform` above). Two-line
            topbar preserved via `flex-wrap: nowrap` + a shrinkable
            greeting column. */}
        {backHref && (
          <Link
            to={backHref || '/dashboard'}
            className="account-back-mobile"
            data-testid="account-back-mobile"
            aria-label={backLabel || 'Back to shop'}
          >
            <ArrowLeft size={14} />
            <span className="account-back-mobile-label">{backLabel || 'Back to shop'}</span>
          </Link>
        )}
      </div>

      <div className="body" data-testid="account-page">
        {flash && (
          <div className="alert success" data-testid="account-flash" style={{ marginBottom: 14 }}>
            <CheckCircle2 size={14} /> {flash}
          </div>
        )}
        {error && (
          <div className="alert error" data-testid="account-error" style={{ marginBottom: 14 }}>
            {error}
          </div>
        )}

        <div className="acct-grid" style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
          gap: 14,
          alignItems: 'start',
          maxWidth: 1100,
        }}>
          {/* PROFILE ------------------------------------------------ */}
          <section className="panel" style={{ padding: 18 }} data-testid="account-profile-panel">
            <label style={{ fontSize: 12.5, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
              <User size={14} /> Your profile
            </label>
            <div className="hint" style={{ marginBottom: 12 }}>Shown to your team and on the shop&rsquo;s public booking page.</div>

            <div className="field">
              <label>Full name</label>
              <input
                className="inp"
                type="text"
                value={fullName}
                onChange={e => setFullName(e.target.value)}
                data-testid="account-name"
              />
            </div>
            <div className="field">
              <label>Phone</label>
              <input
                className="inp"
                type="tel"
                value={phone}
                onChange={e => setPhone(e.target.value)}
                placeholder="+44 7…"
                data-testid="account-phone"
              />
            </div>
            <button
              className="btn p"
              disabled={saving}
              onClick={saveProfile}
              data-testid="account-save-profile"
            >
              {saving ? 'Saving…' : 'Save profile'}
            </button>
          </section>

          {/* EMAIL -------------------------------------------------- */}
          <section className="panel" style={{ padding: 18, marginBottom: 14 }} data-testid="account-email-panel">
            <label style={{ fontSize: 12.5, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
              <Mail size={14} /> Login email
            </label>
            <div className="hint" style={{ marginBottom: 12 }}>
              Your current email is <b style={{ color: 'var(--ink)' }}>{me?.email}</b>.
              {pendingEmail && pendingEmail !== me?.email && (
                <> A change to <b style={{ color: 'var(--ink)' }}>{pendingEmail}</b> is pending — click the verification link we sent to that address.</>
              )}
            </div>

            <div className="field">
              <label>New email</label>
              <input
                className="inp"
                type="email"
                value={newEmail}
                onChange={e => setNewEmail(e.target.value)}
                placeholder="you@newdomain.com"
                data-testid="account-new-email"
              />
            </div>
            <button
              className="btn s"
              disabled={emailBusy || !newEmail}
              onClick={requestEmailChange}
              data-testid="account-request-email"
            >
              {emailBusy ? 'Sending…' : 'Send verification →'}
            </button>
            <p className="hint" style={{ marginTop: 10, marginBottom: 0 }}>
              Your existing login keeps working until you click the verification link at the new address, so a typo won&rsquo;t lock you out.
            </p>
          </section>

          {/* PASSWORD ---------------------------------------------- */}
          <section className="panel" style={{ padding: 18 }} data-testid="account-password-panel">
            <label style={{ fontSize: 12.5, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
              <Lock size={14} /> Password
            </label>
            <div className="hint" style={{ marginBottom: 12 }}>Enter your current password to prove it&rsquo;s you, then set a new one.</div>

            <div className="field">
              <label>Current password</label>
              <PasswordInput
                value={currentPw}
                onChange={e => setCurrentPw(e.target.value)}
                data-testid="account-current-pw"
                autoComplete="current-password"
              />
            </div>
            <div className="field">
              <label>New password</label>
              <PasswordInput
                value={newPw}
                onChange={e => setNewPw(e.target.value)}
                data-testid="account-new-pw"
                autoComplete="new-password"
                showStrength
              />
            </div>
            <button
              className="btn p"
              disabled={pwBusy || !currentPw || !newPw}
              onClick={changePassword}
              data-testid="account-change-pw"
            >
              {pwBusy ? 'Updating…' : 'Change password'}
            </button>
          </section>
        </div>
      </div>
    </main>
  );
};

const Account = () => <AccountShell><AccountInner /></AccountShell>;
export default Account;
